"use client";

import type { Id } from "@convex/_generated/dataModel";
import type {
  BuyAgainCartConflict,
  BuyAgainReview,
} from "@convex/schemas/validators/buyAgain";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

interface BuyAgainCartChoicesProps {
  review: BuyAgainReview;
  keepCart: ReadonlySet<Id<"products">>;
  onChange: (productId: Id<"products">, keepCart: boolean) => void;
}

function ConflictChoice({
  conflict,
  keepCart,
  onChange,
}: {
  conflict: BuyAgainCartConflict;
  keepCart: boolean;
  onChange: (keepCart: boolean) => void;
}) {
  const cartId = `${conflict.productId}-cart`;
  const buyAgainId = `${conflict.productId}-buy-again`;
  return (
    <fieldset className="space-y-1">
      <legend className="text-sm font-medium">{conflict.productName}</legend>
      <RadioGroup
        value={keepCart ? "cart" : "buy-again"}
        onValueChange={(value) => onChange(value === "cart")}
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem id={cartId} value="cart" />
          <Label htmlFor={cartId}>
            In your cart: {conflict.cartQuantity.toLocaleString()}
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem id={buyAgainId} value="buy-again" />
          <Label htmlFor={buyAgainId}>
            Buy again: {conflict.buyAgainQuantity.toLocaleString()}
          </Label>
        </div>
      </RadioGroup>
    </fieldset>
  );
}

export function BuyAgainCartChoices({
  review,
  keepCart,
  onChange,
}: BuyAgainCartChoicesProps) {
  if (review.cartConflicts.length === 0 && review.otherCartItemCount === 0)
    return null;
  return (
    <section className="space-y-3">
      <h3 className="text-overline uppercase text-muted-foreground">
        Already in your cart
      </h3>
      {review.cartConflicts.map((conflict) => (
        <ConflictChoice
          key={conflict.productId}
          conflict={conflict}
          keepCart={keepCart.has(conflict.productId)}
          onChange={(keep) => onChange(conflict.productId, keep)}
        />
      ))}
      {review.otherCartItemCount > 0 ? (
        <p className="text-sm text-muted-foreground">
          Your cart also has {review.otherCartItemCount} other{" "}
          {review.otherCartItemCount === 1 ? "item" : "items"}. They stay in
          your cart and check out with these unless you remove them.
        </p>
      ) : null}
    </section>
  );
}
